import React, { useEffect, useState } from "react";
import { Spin, Table, Skeleton } from "antd";
import { type ColumnsType } from "antd/es/table";
import { TableContainerStyled } from "./subject-data.styled";
import { type ISchedule, type ISection } from "@/Interfaces/subject";
import { useLocale } from "../../../i18n/localeContext";
import { getUserEnrollementPage } from "../../../Api/content";
import { StrapiError } from "../../../pages/StrapiError";
import { _translateValue } from "../../../pages/admin-pages/translation-gestor/translateValue";

const SCREEN_NAME = "PantallaSubjectTableSchedule";

interface ISubjectScheduleTable {
  secciones: ISection[] | undefined;
  isLoading: boolean;
}

export const SubjectTableSchedule = ({ secciones, isLoading }: ISubjectScheduleTable) => {
  const { locale } = useLocale();
  const [userEnrollementPage, setUserEnrollementPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [translatedData, setTranslatedData] = React.useState<{
    modalidades: { [key: string]: string };
    sedes: { [key: string]: string };
  }>({
    modalidades: {},
    sedes: {},
  });
  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setUserEnrollementPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getUserEnrollementPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = secciones && Array.isArray(secciones) && secciones.length > 0;
      if (!hasData) {
        setTranslatedData({ modalidades: {}, sedes: {} });
        return;
      }

      const modalidadesTranslations: { [key: string]: string | null } = {};
      const sedesTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      secciones.forEach((section: ISection) => {
        if (section.modalidadNombre && !modalidadesTranslations.hasOwnProperty(section.modalidadNombre)) {
          modalidadesTranslations[section.modalidadNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, section.modalidadNombre, locale).then((translated) => {
              modalidadesTranslations[section.modalidadNombre] = translated;
            })
          );
        }
        if (section.sedeNombre && !sedesTranslations.hasOwnProperty(section.sedeNombre)) {
          sedesTranslations[section.sedeNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, section.sedeNombre, locale).then((translated) => {
              sedesTranslations[section.sedeNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);
      setTranslatedData({
        modalidades: modalidadesTranslations as { [key: string]: string },
        sedes: sedesTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [secciones, locale]);

  const columns: ColumnsType<ISection> = [
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[0]?.label || "Sección"),
      dataIndex: "asignaturaSeccionCodigo",
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[1]?.label || "Horario"),
      dataIndex: "horarios",
      render: (value) =>
        value
          ?.map((hr: ISchedule) => `${hr.diaNombre} ${hr.horaInicio} - ${hr.horaFin}`)
          .toString() ?? "-",
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[2]?.label || "Modalidad"),
      dataIndex: "modalidadNombre",
      render: (text: string) => translatedData.modalidades[text] || text,
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[3]?.label || "Precio"),
      dataIndex: "seccionPrecio",
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[4]?.label || "Profesor"),
      dataIndex: "profesorNombre",
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[5]?.label || "Sede"),
      dataIndex: "sedeNombre",
      render: (text: string) => translatedData.sedes[text] || text,
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[6]?.label || "Cupo"),
      dataIndex: "cupo",
    },
    {
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection_table?.[7]?.label || "Acciones"),
      dataIndex: "acciones",
    },
  ];

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setUserEnrollementPage(null);
      }}
    />;
  }

  return (
    <TableContainerStyled>
      <Spin spinning={isLoading} size="large">
        <Table
          columns={columns}
          dataSource={secciones}
          size="middle"
          bordered
          pagination={false}
          rowKey={(v) => `${v.asignaturaSeccionCodigo} ${v.seccionId}`}
        />
      </Spin>
    </TableContainerStyled>
  );
};
